import { useEffect, useRef, useState, type MouseEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Popover } from "radix-ui";
import type { MentionCandidate, Post, PostDraft, UserSummary } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { fullDate, relative } from "../../lib/format";
import { Dialog } from "../../components/ui/Dialog";
import { Button, IconButton } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Settings";
import { Switch, Tabs, EmptyState } from "../../components/ui/Controls";
import { Spinner } from "../../components/ui/Spinner";
import { toast } from "../../components/ui/Toast";
import { UserAvatar } from "../../components/ui/Avatar";
import { IconPlus, IconTrash, IconX } from "../../components/icons";
import { IconDraft, IconSchedule } from "../../components/socialIcons";

/* ───────────── Poll editor ───────────── */

export interface PollDraft {
  options: string[];
  multiple: boolean;
  durationHours: number;
}
export const emptyPoll = (): PollDraft => ({ options: ["", ""], multiple: false, durationHours: 24 });

const DURATIONS = [
  { value: "1", label: "1 hour" },
  { value: "6", label: "6 hours" },
  { value: "24", label: "1 day" },
  { value: "72", label: "3 days" },
  { value: "168", label: "1 week" },
  { value: "336", label: "2 weeks" },
];

export function PollEditor({ poll, onChange, onRemove }: { poll: PollDraft; onChange: (p: PollDraft) => void; onRemove: () => void }) {
  const set = (i: number, v: string) => onChange({ ...poll, options: poll.options.map((o, j) => (j === i ? v : o)) });
  return (
    <div className="mt-3 space-y-2 rounded-lg border border-line p-3" role="group" aria-label="Poll">
      {poll.options.map((o, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={o} maxLength={80} onChange={(e) => set(i, e.target.value)} placeholder={`Option ${i + 1}`} aria-label={`Option ${i + 1}`} className="!h-9" />
          {poll.options.length > 2 && (
            <IconButton size="sm" label={`Remove option ${i + 1}`} icon={IconX} onClick={() => onChange({ ...poll, options: poll.options.filter((_, j) => j !== i) })} />
          )}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1">
        {poll.options.length < 6 && (
          <Button size="sm" variant="ghost" icon={IconPlus} onClick={() => onChange({ ...poll, options: [...poll.options, ""] })}>
            Add option
          </Button>
        )}
        <Select aria-label="Poll length" value={String(poll.durationHours)} onChange={(v) => onChange({ ...poll, durationHours: Number(v) })} options={DURATIONS} />
        <label className="flex items-center gap-2 text-sm text-fg-2">
          <Switch label="Allow several choices" checked={poll.multiple} onCheckedChange={(v) => onChange({ ...poll, multiple: v })} />
          Several choices
        </label>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={onRemove}>
          Remove poll
        </Button>
      </div>
    </div>
  );
}

/* ───────────── Schedule picker ───────────── */

/** Local "YYYY-MM-DDTHH:mm" for datetime-local inputs. */
export function toLocalInput(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function SchedulePicker({ value, onChange }: { value: string | null; onChange: (iso: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState("");
  useEffect(() => {
    if (open) setLocal(value ? toLocalInput(new Date(value)) : toLocalInput(new Date(Date.now() + 3600_000)));
  }, [open, value]);
  const at = local ? new Date(local) : null;
  const valid = !!at && !isNaN(+at) && at.getTime() > Date.now() + 30_000;
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <IconButton label={value ? `Scheduled for ${fullDate(value)}` : "Schedule"} icon={IconSchedule} active={!!value} className={cn(value && "text-accent")} />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content side="top" align="start" sideOffset={6} collisionPadding={8} className="z-[60] w-72 space-y-3 rounded-lg bg-surface p-3 shadow-pop animate-pop-in">
          <label className="block text-sm font-medium text-fg-2" htmlFor="schedule-at">
            Publish at
          </label>
          <Input id="schedule-at" type="datetime-local" value={local} min={toLocalInput(new Date())} onChange={(e) => setLocal(e.target.value)} className="!h-9" />
          {!valid && local && <p className="text-xs text-danger">Pick a time in the future.</p>}
          <div className="flex justify-end gap-2">
            {value && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                Clear
              </Button>
            )}
            <Button
              size="sm"
              variant="primary"
              disabled={!valid}
              onClick={() => {
                onChange(at!.toISOString());
                setOpen(false);
              }}
            >
              Set time
            </Button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/* ───────────── Tag people on an image ───────────── */

export interface PendingTag {
  user: UserSummary;
  x: number;
  y: number;
}

export function TagPeopleDialog({ open, onOpenChange, image, mediaId, tags, onSaved }: { open: boolean; onOpenChange: (o: boolean) => void; image: string; mediaId: string; tags: PendingTag[]; onSaved: (t: PendingTag[]) => void }) {
  const [list, setList] = useState<PendingTag[]>(tags);
  const [point, setPoint] = useState<{ x: number; y: number } | null>(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    if (open) {
      setList(tags);
      setPoint(null);
      setQ("");
    }
  }, [open, tags]);
  const results = useQuery({
    queryKey: ["mention", q],
    queryFn: () => api.get<{ items: MentionCandidate[] }>(`/api/users/autocomplete?q=${encodeURIComponent(q)}`).then((r) => r.items),
    enabled: open && !!point && q.trim().length > 0,
  });

  const place = (e: MouseEvent<HTMLImageElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPoint({ x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) });
    setQ("");
  };
  const add = (u: UserSummary) => {
    if (!point) return;
    setList((l) => [...l.filter((t) => t.user.id !== u.id), { user: u, ...point }].slice(0, 10));
    setPoint(null);
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/api/media/${mediaId}/tags`, { tags: list.map((t) => ({ userId: t.user.id, x: t.x, y: t.y })) });
      onSaved(list);
      onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't save tags"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Tag people"
      description="Click the image where someone appears."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void save()}>
            Done
          </Button>
        </>
      }
    >
      <div className="relative mx-auto w-fit max-w-full">
        <img ref={imgRef} src={image} alt="" onClick={place} className="block max-h-[50dvh] w-auto max-w-full cursor-crosshair rounded-md" />
        {list.map((t) => (
          <span key={t.user.id} className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-black/75 px-1.5 py-0.5 text-xs font-medium text-white" style={{ left: `${t.x * 100}%`, top: `${t.y * 100}%` }}>
            {t.user.displayName}
          </span>
        ))}
        {point && <span className="absolute size-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow" style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }} aria-hidden />}
      </div>
      {point && (
        <div className="mt-3">
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Who's this?" aria-label="Search people to tag" className="!h-9" />
          <ul className="mt-1 max-h-48 overflow-y-auto">
            {results.data?.map((u) => (
              <li key={u.id}>
                <button type="button" onClick={() => add(u)} className="flex h-10 w-full items-center gap-2.5 rounded-md px-2 text-left hover:bg-surface">
                  <UserAvatar user={u} size="sm" plain />
                  <span className="truncate text-sm font-medium">{u.displayName}</span>
                  <span className="truncate text-xs text-fg-3">@{u.username}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {list.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-1.5">
          {list.map((t) => (
            <li key={t.user.id} className="inline-flex h-7 items-center gap-1 rounded-md bg-surface-2 pl-2 pr-0.5 text-sm">
              {t.user.displayName}
              <IconButton size="sm" label={`Remove tag for ${t.user.displayName}`} icon={IconX} className="!size-6" onClick={() => setList((l) => l.filter((x) => x.user.id !== t.user.id))} />
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

/* ───────────── Drafts & scheduled posts ───────────── */

export function useDrafts(enabled: boolean) {
  return useQuery({ queryKey: ["drafts"], queryFn: () => api.get<{ items: PostDraft[] }>("/api/drafts").then((r) => r.items), enabled });
}

function draftText(d: PostDraft) {
  const p = d.payload as { content?: string; mediaIds?: string[]; poll?: unknown };
  return p.content?.trim() || (p.poll ? "Poll" : p.mediaIds?.length ? `${p.mediaIds.length} attachment${p.mediaIds.length > 1 ? "s" : ""}` : "Empty draft");
}

function DraftRow({ d, onResume, onChanged }: { d: PostDraft; onResume: (d: PostDraft) => void; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    try {
      await fn();
      if (done) toast.show(done);
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="flex items-start gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm text-fg">{draftText(d)}</p>
        <p className={cn("mt-0.5 text-xs", d.status === "failed" ? "text-danger" : "text-fg-3")}>
          {d.status === "scheduled" && d.scheduledAt ? `Publishes ${relative(d.scheduledAt)} · ${fullDate(d.scheduledAt)}` : d.status === "failed" ? `Couldn't publish: ${d.error ?? "unknown error"}` : `Edited ${relative(d.updatedAt)}`}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {d.status === "scheduled" && (
          <>
            <SchedulePicker value={d.scheduledAt} onChange={(iso) => void run(() => api.patch(`/api/drafts/${d.id}`, { ...d.payload, scheduledAt: iso }), iso ? "Rescheduled" : "Moved to drafts")} />
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void run(() => api.patch(`/api/drafts/${d.id}`, { ...d.payload, scheduledAt: null }), "Schedule cancelled")}>
              Cancel
            </Button>
          </>
        )}
        {d.status !== "scheduled" && (
          <Button size="sm" variant="ghost" onClick={() => onResume(d)}>
            Edit
          </Button>
        )}
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => void run(() => api.post<Post>(`/api/drafts/${d.id}/publish`), "Posted")}>
          Post now
        </Button>
        <IconButton size="sm" label="Delete draft" icon={IconTrash} tone="danger" disabled={busy} onClick={() => void run(() => api.del(`/api/drafts/${d.id}`))} />
      </div>
    </li>
  );
}

export function DraftsDialog({ open, onOpenChange, onResume }: { open: boolean; onOpenChange: (o: boolean) => void; onResume: (d: PostDraft) => void }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<"drafts" | "scheduled">("drafts");
  const drafts = useDrafts(open);
  const items = (drafts.data ?? []).filter((d) => (tab === "scheduled" ? d.status === "scheduled" : d.status !== "scheduled"));
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["drafts"] });
    void qc.invalidateQueries({ queryKey: ["feed"] });
    void qc.invalidateQueries({ queryKey: ["profile-posts"] });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Drafts" size="lg">
      <Tabs
        ariaLabel="Drafts"
        value={tab}
        onChange={setTab}
        className="-mx-3 -mt-3"
        options={[
          { value: "drafts", label: "Drafts" },
          { value: "scheduled", label: "Scheduled" },
        ]}
      />
      {drafts.isPending ? (
        <div className="flex h-24 items-center justify-center">
          <Spinner className="size-4 text-fg-3" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={tab === "scheduled" ? IconSchedule : IconDraft} title={tab === "scheduled" ? "Nothing scheduled" : "No drafts"} className="py-8" />
      ) : (
        <ul className="divide-y divide-line">
          {items.map((d) => (
            <DraftRow key={d.id} d={d} onChanged={refresh} onResume={(x) => (onResume(x), onOpenChange(false))} />
          ))}
        </ul>
      )}
    </Dialog>
  );
}
